import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, type CSSProperties } from "react";
import { useReducedMotion } from "motion/react";
import { cn } from "@repo/blacksmith-ui/cn";

/** Per row: how many VMs run now, then how many wait for off-peak. The rest sit idle. */
export type FleetRow = readonly [running: number, offPeak: number];
const CELL_CLASS = {
  running: "bg-neutral-900 dark:bg-neutral-100",
  offPeak: "bg-neutral-400 dark:bg-neutral-500",
  idle: "bg-neutral-200 dark:bg-neutral-700"
} as const;
type FleetCellKind = keyof typeof CELL_CLASS;
export function fleetCellKind([running, offPeak]: FleetRow, column: number): FleetCellKind {
  if (column < running) return "running";
  return column < running + offPeak ? "offPeak" : "idle";
}
export const SANDBOX_MARK_COLUMNS = 4;
export const SANDBOX_MARK_ROWS: readonly FleetRow[] = [[3, 1], [2, 1], [3, 0], [1, 2]];
export const SANDBOX_STRIP_ROWS: readonly FleetRow[] = [[14, 3], [10, 7], [8, 9], [11, 4]];
export const SANDBOX_SPLASH_ROWS: readonly FleetRow[] = [[17, 4], [12, 8], [20, 2], [9, 10], [14, 5]];
const DRIFT_INTERVAL_MS = 675;
/** How far a row's counts may wander from its starting shape, so the art keeps its silhouette. */
const DRIFT_RANGE = 3;
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const randomStep = () => Math.random() < 0.5 ? -1 : 1;
function driftRow([running, offPeak]: FleetRow, [baseRunning, baseOffPeak]: FleetRow, columns: number): FleetRow {
  const nextRunning = clamp(running + randomStep(), Math.max(0, baseRunning - DRIFT_RANGE), baseRunning + DRIFT_RANGE);
  const nextOffPeak = clamp(offPeak + randomStep(), Math.max(0, baseOffPeak - DRIFT_RANGE), Math.min(baseOffPeak + DRIFT_RANGE, columns - nextRunning));
  return [nextRunning, nextOffPeak];
}

/** Every tick one row starts or stops a VM or two, like a fleet under load. */
function useDriftingRows(rows, columns, animate) {
  const $ = _c(5);
  const reduceMotion = useReducedMotion();
  const drifting = animate && !reduceMotion;
  const [current, setCurrent] = useState(rows);
  let t0;
  let t1;
  if ($[0] !== columns || $[1] !== drifting || $[2] !== rows) {
    t0 = () => {
      if (!drifting) {
        return;
      }
      const id = window.setInterval(() => {
        if (document.hidden) {
          return;
        }
        setCurrent(previous => {
          const target = Math.floor(Math.random() * previous.length);
          return previous.map((row, index) => index === target ? driftRow(row, rows[index] ?? row, columns) : row);
        });
      }, DRIFT_INTERVAL_MS);
      return () => window.clearInterval(id);
    };
    t1 = [drifting, rows, columns];
    $[0] = columns;
    $[1] = drifting;
    $[2] = rows;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  return drifting ? current : rows;
}

/** A fleet of VMs as a grid of cells, for the Sandboxes tile and page art. */
export function SandboxFleet(t0) {
  const $ = _c(17);
  const {
    rows,
    columns,
    animate: t1,
    className,
    cellClassName
  } = t0;
  const animate = t1 === undefined ? false : t1;
  const shownRows = useDriftingRows(rows, columns, animate);
  const t2 = `repeat(${columns}, minmax(0, 1fr))`;
  let t3;
  if ($[0] !== t2) {
    t3 = {
      gridTemplateColumns: t2
    };
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const style = t3;
  let t4;
  if ($[2] !== className) {
    t4 = cn("grid", className);
    $[2] = className;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== animate || $[5] !== cellClassName || $[6] !== columns || $[7] !== shownRows) {
    let t6;
    if ($[9] !== animate || $[10] !== cellClassName || $[11] !== columns) {
      t6 = (fleetRow, row) => Array.from({
        length: columns
      }, (_, column) => <span key={`${row}-${column}`} className={cn("rounded-[2px]", animate && "transition-colors duration-[375ms] ease-in-out", CELL_CLASS[fleetCellKind(fleetRow, column)], cellClassName)} />);
      $[9] = animate;
      $[10] = cellClassName;
      $[11] = columns;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    t5 = shownRows.flatMap(t6);
    $[4] = animate;
    $[5] = cellClassName;
    $[6] = columns;
    $[7] = shownRows;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[13] !== style || $[14] !== t4 || $[15] !== t5) {
    t6 = <div aria-hidden={true} className={t4} style={style}>{t5}</div>;
    $[13] = style;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}